import * as React from "react";

import classNames from "classnames";
import {useSelector} from "react-redux";
import styled from "styled-components";

import {AdviceCard} from "./AdviceCard";
import {AdviceCardTitle} from "./AdviceCardTitle";
import {AdviceInformation} from "./AdviceInformation";
import {useAdvice} from "./useAdvice";
import {selectUserStateFromMainState} from "../../Store/UserState";
import {Colors, Fonts} from "../../Styles";
import { GTMDataLayerPush } from "../../Util/GoogleTagManager";
import {hasFeature} from "../../Util/UserUtils";
import {AdviceProps} from "../Advices/Util/Props";
import {RoundButton} from "../Buttons";
import {AcceptAllIcon, AcceptIcon, DictionaryIcon, TrashIcon} from "../Icons";
import {Pill} from "../Pill";
import {Tooltip} from "../Tooltip/Tooltip";


import '../../Styles/component/authoring/advice/advice.sass';


interface Props {
    data: AdviceProps;
    adviceType: "SPELL-ERROR" | "STYLE-ERROR";
    adviceHeader: React.ReactNode;
}

export type cardTypes = "examples" | "additionalInfo";

export const StyledAdvice = styled.div`
    margin-bottom: 30px;

    details.always-open {
        z-index: 3;
        margin-top: 0;
        padding-top: 15px;
        padding-bottom: 20px;
        border-radius: 10px;
    }

    details:nth-child(2) {
        z-index: 2;
    }

    details:nth-child(3) {
        z-index: 1;
    }

    .button-navigation {
        display: flex;

        > * + * {
            margin-left: 13px;
        }
    }
`

const StyledTextTruncate = styled.span`
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    display: -webkit-box;
    overflow: hidden;
`;

const StyledOriginalError = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px 0 10px;
    font-family: ${Fonts.publico};
    font-size: 23px;
    line-height: 1.2;

    .original-error {
        background-color: ${Colors.visVis};
        line-height: 1;
        word-wrap: break-word;
        -webkit-hyphens: auto;
        -ms-hyphens: auto;
        -moz-hyphens: auto;
        hyphens: auto;
        &.style {
            background-color: ${Colors.periwinkle};
        }
    }
`;

const StyledAdditionalReplacements = styled.div`
    margin-top: 25px;

    h3 {
        text-transform: uppercase;
        font-family: ${Fonts.theinhardt};
        font-size: 14px;
        font-weight: bold;
        color: ${Colors.osloGray};
        line-height: 1.38;
    }
`;

const StyledAdvicePanelButton = styled.button`
    border: none;
    background: ${Colors.white};
    color: ${Colors.mineShaft};
    cursor: pointer;
    padding: 7px 15px 7px 2px;
    text-align: left;
    display: flex;
    align-items: center;
    font-size: 15px;
    width: 100%;
    margin: 0 -3px 3px;
    border-radius: 22px;

    &:first-of-type {
        margin-top: 15px;
    }

    &:hover {
        background: ${Colors.mystic};
    }

    svg {
        margin-right: 7px;
        margin-left: 5px;
        vertical-align: middle;
        width: 29px;
        height: 29px;
    }
`;

export const truncateText = (text: string): string => {
    const maxStringLength: number = 90;
    if (text.length <= maxStringLength) { return text }
    const subString = text.substr(0, maxStringLength-1);
    return `${subString.substr(0, subString.lastIndexOf(" "))} …`;
}

export const Advice = (props: Props) => {
    const [openedCard, setOpenedCard] = React.useState<undefined | cardTypes>(undefined);
    const {
        suggestion,
        canAddToDictionary,
        isMultiple,
        isAcceptable,
        accept,
        mouseEnter,
        mouseLeave,
        addToDictionary,
        reject,
    } = useAdvice(props.data, props.adviceType);

    // @see https://jira.cornelsen.de/browse/DUMEN-492
    const user = useSelector(selectUserStateFromMainState);
    const debugErrorCodes = hasFeature('debugErrorCodes', user.features);

    const toggleCard = (event: React.MouseEvent<HTMLDetailsElement>, value: cardTypes) => {
        event.preventDefault();
        if (value) {
            GTMDataLayerPush({event: "open-card", "advice-type": props.adviceType === "SPELL-ERROR" ? "spelling" : "style", location: "advice-panel", card: value})
        }
        setOpenedCard(value === openedCard ? undefined : value);
    }

    const onClickAccept = (alternative: string) => {
        GTMDataLayerPush({event: "text-advice", option: "accepted", location: "advice-panel", "advice-type": props.adviceType === "SPELL-ERROR" ? "spelling": "style"});
        accept(alternative);
    }

    const onClickAlternatives = (alternative: string) => {
        GTMDataLayerPush({event: "select-alternative", "advice-type": props.adviceType === "SPELL-ERROR" ? "spelling" : "style", location: "advice-panel"});
        accept(alternative);
    }

    const onClickReject = () => {
        GTMDataLayerPush({event: "text-advice", option: "rejected", location: "advice-panel", "advice-type": props.adviceType === "SPELL-ERROR" ? "spelling": "style"});
        reject();
    }

    const onClickDictionary = () => {
        GTMDataLayerPush({event: "save-in-dictionary", location: "advice-panel", "advice-type": props.adviceType === "SPELL-ERROR" ? "spelling": "style"});
        addToDictionary();
    }

    return (
        <StyledAdvice>
            <AdviceCard alwaysOpen={true}>
                <AdviceCardTitle onClick={(e) => e.preventDefault()}>
                    {props.adviceHeader}
                </AdviceCardTitle>

                <React.Fragment>
                    <StyledOriginalError>
                        <StyledTextTruncate>
                            <span className={classNames("original-error", {
                                style: props.data.type === "style",
                            })}>{truncateText(props.data.originalError)}</span>
                        </StyledTextTruncate>

                    </StyledOriginalError>
                </React.Fragment>
                {isAcceptable && (
                    <p style={{fontFamily: Fonts.theinhardt, fontSize: 18, margin: "5px 0", paddingBottom: 10}}>
                        {props.data.shortMessage}
                    </p>
                )}
                 {(debugErrorCodes) && (
                    <p style={{color: Colors.silver}}> (Fehlercode: <a href={`/admin/error_message/list?name=${props.data.errorCode}`} target="_blank">{props.data.errorCode}</a>)</p>
                )}

                <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 13, paddingTop: 13, borderTop: `1px solid ${Colors.iron}`}}>
                    {suggestion}
                    <div className="button-navigation">
                        {isAcceptable && (
                            <Tooltip direction="west" text={isMultiple ? "Alle Vorschläge übernehmen" : "Vorschlag übernehmen"}>
                                <RoundButton
                                    onClick={() => onClickAccept(props.data.proposals[0] || ' ')}
                                    onMouseEnter={() => mouseEnter(props.data.proposals[0] || ' ')}
                                    onMouseLeave={mouseLeave}
                                    defaultBackground={Colors.supernova}
                                    hoverBackground={Colors.pastelGreen}
                                    iconHoverColor={Colors.white}
                                >
                                    {isMultiple ? <AcceptAllIcon/> : <AcceptIcon/>}
                                </RoundButton>
                            </Tooltip>
                        )}
                    </div>
                </div>

                {(props.data.proposals.length > 1 || (props.data.synonyms && props.data.synonyms.length > 0)) && (
                    <StyledAdditionalReplacements>
                        <h3>Alternativen</h3>
                        {(props.data.proposals.length > 1
                                ? props.data.proposals.slice(1)
                                : props.data.synonyms.reduce((previousValue: string[], currentValue) => previousValue.concat(currentValue.synonyms), [])
                                    .filter((currentValue: string, currentIndex: number, synonyms: string[]) => synonyms.indexOf(currentValue) === currentIndex) //hotfix until api is fixed
                        ).slice(0, 10)
                            .map((alternative, index) => (
                                <Pill
                                    key={index}
                                    onClick={() => onClickAlternatives(alternative)}
                                    onMouseEnter={() => mouseEnter(alternative)}
                                    onMouseLeave={mouseLeave}
                                >{alternative}</Pill>
                            ))
                        }
                    </StyledAdditionalReplacements>
                )}
                <StyledAdvicePanelButton onClick={onClickReject}>
                    <TrashIcon /> Ignorieren
                </StyledAdvicePanelButton>

                {canAddToDictionary && (
                     <StyledAdvicePanelButton onClick={onClickDictionary}>
                        <DictionaryIcon /> In meinem Wörterbuch speichern
                    </StyledAdvicePanelButton>
                )}
            </AdviceCard>

            {props.data.errorMessage && (
                <AdviceCard open={openedCard === "examples"}>
                    <AdviceCardTitle isExpandable onClick={(e) => toggleCard(e, "examples")}>Erklärung und Beispiele</AdviceCardTitle>
                    <AdviceInformation dangerouslySetInnerHTML={{__html: props.data.errorMessage}}/>
                </AdviceCard>
            )}

            {(props.data.additionalInformation) && (
                <AdviceCard open={openedCard === "additionalInfo"}>
                    <AdviceCardTitle isExpandable onClick={(e) => toggleCard(e, "additionalInfo")}>Mehr Informationen</AdviceCardTitle>
                    <AdviceInformation dangerouslySetInnerHTML={{__html: props.data.additionalInformation}}/>
                </AdviceCard>
            )}
        </StyledAdvice>
    );
}
